import type { Rng } from './rng';
import { Snowfall, type SnowfallId } from './snow';
import { climateRange, DUST_WIND, diurnalPercent, MapTheme, mapTheme } from './theme';

/*
 * Weather beyond the wind: sky condition, visibility and temperature. It changes hourly as a
 * small Markov chain: fog forms on calm nights and burns off in the morning, precipitation
 * falls as snow when it is cold (less often than rain, a few hours at a time, light, moderate
 * or heavy: see snow.ts). Temperatures follow a daily cycle around a slowly drifting "climate"
 * value, from integer tables (no Math.sin in the simulation). The map's theme (theme.ts) sets
 * the odds: the desert's dust storms and dry skies, the mountains' snow and valley fog.
 */

/** Sky condition. Saved: append only. Dust: a dust storm, on the desert map only. */
export const Sky = { Clear: 0, Cloudy: 1, Rain: 2, Fog: 3, Snow: 4, Storm: 5, Dust: 6 } as const;
export type SkyId = (typeof Sky)[keyof typeof Sky];

/** Offset from the day's mean temperature (°C) for each hour, coolest before dawn. */
const DIURNAL = [
  -4, -5, -5, -6, -6, -6, -5, -4, -2, 0, 1, 3, 4, 5, 5, 5, 4, 3, 2, 0, -1, -2, -3, -4,
] as const;

/** Thunderstorms only build in warm weather (°C). */
export const STORM_MIN_TEMPERATURE = 18;

/** A new game starts on a mild day under a clear sky. */
export const INITIAL_SKY: SkyId = Sky.Clear;
export const INITIAL_VISIBILITY = 100;
export const INITIAL_CLIMATE = 12;
/** Day-to-day mean temperature stays in this range (°C), drifting towards the season's. */

/** Lowest visibility for an unlit runway (visual approaches only), in 100 m units. */
export const VISUAL_MINIMUM = 8;
/** Lit runways (level 2) have a CAT I ILS: down to about 550 m. */
export const CAT_I_MINIMUM = 6;
/** Lowest visibility at which even an ILS runway operates (CAT III), in 100 m units. */
export const ILS_MINIMUM = 1;

export interface WeatherState {
  sky: SkyId;
  /** Visibility in units of 100 m (100 = 10 km or more). */
  visibility: number;
  /** Mean temperature of the current days (°C); the hour adds the daily cycle. */
  climate: number;
  /** How hard it snows (Snowfall) while the sky is snow; none otherwise. */
  snowfall: number;
  /** Wind (see wind.ts): fog only forms in light winds, dust storms in strong ones. */
  readonly windSpeed: number;
  /** The map (MapTheme): its weather odds and temperatures. */
  readonly theme: number;
}

/** Air temperature (°C) at the given hour of the day (the swing by the map: theme.ts). */
export function temperatureAt(climate: number, hour: number, theme = 0): number {
  const swing = DIURNAL[((hour % 24) + 24) % 24] ?? 0;
  return climate + Math.trunc((swing * diurnalPercent(theme)) / 100);
}

/** Freezing precipitation or fog: aircraft must be de-iced before take-off. */
export function icing(w: Pick<WeatherState, 'sky' | 'climate' | 'theme'>, hour: number): boolean {
  const wet = w.sky === Sky.Snow || w.sky === Sky.Rain || w.sky === Sky.Fog;
  return wet && temperatureAt(w.climate, hour, w.theme) <= 1;
}

/** Lowest visibility (100 m units) a runway of this level can operate in. */
export function visibilityMinimum(runwayLevel: number): number {
  if (runwayLevel >= 3) return ILS_MINIMUM;
  return runwayLevel >= 2 ? CAT_I_MINIMUM : VISUAL_MINIMUM;
}

/** Visibility in snow (100 m units), by snowfall: the heavier, the less one sees. */
const SNOW_VISIBILITY: readonly (readonly [number, number])[] = [
  [15, 25],
  [15, 25],
  [10, 18],
  [6, 12],
  [2, 5],
];

function visibilityFor(sky: SkyId, snowfall: number, rng: Rng): number {
  switch (sky) {
    case Sky.Clear:
      return rng.range(80, 100);
    case Sky.Cloudy:
      return rng.range(50, 90);
    case Sky.Rain:
      return rng.range(25, 60);
    case Sky.Snow: {
      const [low, high] = SNOW_VISIBILITY[snowfall] ?? [9, 25];
      return rng.range(low, high);
    }
    case Sky.Storm:
      return rng.range(15, 40);
    case Sky.Dust:
      return rng.range(3, 20); // 300 m to 2 km of blowing sand
    default:
      return rng.range(2, 7); // fog: 200–700 m (the thinnest still workable on CAT I)
  }
}

/**
 * Once a day (at midnight): the mean temperature drifts towards the season's typical one,
 * half the way each day (a new season's weather sets in within its first days).
 */
export function updateClimate(w: WeatherState, rng: Rng, typical: number): void {
  const pull = Math.trunc((typical - w.climate) / 2);
  const [low, high] = climateRange(w.theme);
  w.climate = Math.max(low, Math.min(high, w.climate + pull + rng.range(-3, 3)));
}

/** Hours of sky forecast kept ahead, like a TAF. */
export const FORECAST_HOURS = 6;
/**
 * Chance (percent) each hour that the forecast for one hour is revised. An hour far ahead is
 * revised more often before it comes, so forecasts are less sure the further out they look.
 */
export const FORECAST_REVISE_PERCENT = 5;

/** Once an hour: the sky changes, then visibility follows it. */
export function updateWeather(w: WeatherState, rng: Rng, hour: number): void {
  setSky(w, nextSky(w, w.sky, rng, hour), rng);
}

/**
 * Once an hour, with a forecast: the forecast for this hour comes true, the rest is revised
 * now and then, and one more hour is forecast. `skyAhead[i]` is the sky expected at hour
 * `hour + 1 + i` afterwards. Forecasts further out use today's wind and temperatures.
 */
export function updateWeatherAhead(
  w: WeatherState & { readonly skyAhead: number[] },
  rng: Rng,
  hour: number,
): void {
  const ahead = w.skyAhead;
  // A new game (or an older save) has no forecast yet: make one up to this hour.
  while (ahead.length < FORECAST_HOURS) {
    const prev = (ahead.length > 0 ? ahead[ahead.length - 1] : w.sky) as SkyId;
    ahead.push(nextSky(w, prev, rng, (hour + ahead.length) % 24));
  }
  // A revision changes that hour and everything forecast after it (one chain of weather).
  for (let i = 0; i < ahead.length; i++) {
    if (rng.int(100) >= FORECAST_REVISE_PERCENT) continue;
    for (let j = i; j < ahead.length; j++) {
      const prev = (j > 0 ? ahead[j - 1] : w.sky) as SkyId;
      ahead[j] = nextSky(w, prev, rng, (hour + j) % 24);
    }
    break;
  }
  // The temperature may have changed since it was forecast (a new day's climate): rain or snow,
  // and no thunderstorm in the cold.
  setSky(w, settled(w, ahead.shift() as SkyId, hour), rng);
  const last = (ahead.length > 0 ? ahead[ahead.length - 1] : w.sky) as SkyId;
  ahead.push(nextSky(w, last, rng, (hour + FORECAST_HOURS) % 24));
}

function settled(w: Pick<WeatherState, 'climate' | 'theme'>, sky: SkyId, hour: number): SkyId {
  const t = temperatureAt(w.climate, hour, w.theme);
  if (sky === Sky.Rain || sky === Sky.Snow) return t <= 1 ? Sky.Snow : Sky.Rain;
  if (sky === Sky.Storm && t < STORM_MIN_TEMPERATURE - 6) return Sky.Rain;
  return sky;
}

function setSky(w: WeatherState, next: SkyId, rng: Rng): void {
  // A snowfall is light, moderate or heavy from its first hour to its last.
  if (next !== Sky.Snow) w.snowfall = Snowfall.None;
  else if (w.sky !== Sky.Snow || w.snowfall === Snowfall.None) w.snowfall = snowfallFor(rng);
  if (next !== w.sky || rng.int(100) < 30) w.visibility = visibilityFor(next, w.snowfall, rng);
  w.sky = next;
}

/** How hard a new snowfall comes down: mostly light or moderate, now and then heavy. */
function snowfallFor(rng: Rng): SnowfallId {
  const roll = rng.int(100);
  return roll < 45 ? Snowfall.Light : roll < 85 ? Snowfall.Moderate : Snowfall.Heavy;
}

/** The sky in the hour after `sky`, at hour of the day `hour` (one step of the chain). */
export function nextSky(
  w: Pick<WeatherState, 'climate' | 'windSpeed' | 'theme'>,
  sky: SkyId,
  rng: Rng,
  hour: number,
): SkyId {
  const theme = mapTheme(w.theme);
  if (theme === MapTheme.Desert) return desertSky(w, sky, rng, hour);
  const calmNight = w.windSpeed <= 6 && (hour >= 22 || hour <= 7);
  // In the mountains the valley fills with fog on calm nights more often.
  const fogNight = theme === MapTheme.Alpine ? 12 : 5;
  // Clouds only now and then turn to rain, and showers soon pass. Winter is grey: in the cold
  // the sky clouds over more often, clouds bring snow more readily and a snowfall lingers.
  const cold = temperatureAt(w.climate, hour, w.theme) <= 1;
  const roll = rng.int(100);
  let next: SkyId = sky;
  switch (sky) {
    case Sky.Clear:
      if (calmNight && roll < fogNight) next = Sky.Fog;
      else if (roll >= (cold ? 84 : 88)) next = Sky.Cloudy;
      break;
    case Sky.Cloudy:
      if (calmNight && roll < 3) next = Sky.Fog;
      else if (roll >= (cold ? 90 : 85)) next = Sky.Clear;
      else if (roll >= (cold ? 75 : 80)) next = Sky.Rain;
      break;
    case Sky.Rain:
      if (roll < 30) next = Sky.Cloudy;
      // Warm showers can build into thunderstorms.
      else if (roll < 44 && temperatureAt(w.climate, hour) >= STORM_MIN_TEMPERATURE)
        next = Sky.Storm;
      break;
    case Sky.Snow:
      // Mountain snowfalls last longer.
      if (roll < (theme === MapTheme.Alpine ? 14 : 20)) next = Sky.Cloudy;
      break;
    case Sky.Storm:
      // A storm passes in an hour or two, leaving rain behind.
      if (roll < 55) next = Sky.Rain;
      break;
    case Sky.Dust:
      // (An older desert save's dust on another map: it settles.)
      next = Sky.Clear;
      break;
    case Sky.Fog:
      // Burns off once the sun is up; a breeze clears it too.
      if ((hour >= 9 && roll < 35) || (hour < 9 && roll < 6) || w.windSpeed > 10) next = Sky.Cloudy;
      break;
  }
  // Precipitation falls as snow in the cold.
  if (next === Sky.Rain || next === Sky.Snow) {
    next = temperatureAt(w.climate, hour, w.theme) <= 1 ? Sky.Snow : Sky.Rain;
  }
  return next;
}

/**
 * The desert's sky: clear nearly always; a dust storm rises on a windy hour (more readily in
 * the afternoon heat) and settles as the wind drops; clouds are few, a shower rarer still,
 * and no fog or snow.
 */
function desertSky(
  w: Pick<WeatherState, 'climate' | 'windSpeed' | 'theme'>,
  sky: SkyId,
  rng: Rng,
  hour: number,
): SkyId {
  const roll = rng.int(100);
  const windy = w.windSpeed >= DUST_WIND;
  const afternoon = hour >= 12 && hour <= 18;
  switch (sky) {
    case Sky.Clear:
      if (windy && roll < (afternoon ? 30 : 18)) return Sky.Dust;
      if (roll >= 96) return Sky.Cloudy;
      return Sky.Clear;
    case Sky.Cloudy:
      if (windy && roll < 12) return Sky.Dust;
      if (roll >= 70) return Sky.Clear;
      if (roll >= 66) return Sky.Rain;
      return Sky.Cloudy;
    case Sky.Rain:
      if (roll < 55) return Sky.Cloudy;
      if (roll < 62 && temperatureAt(w.climate, hour, w.theme) >= STORM_MIN_TEMPERATURE)
        return Sky.Storm;
      return Sky.Rain;
    case Sky.Storm:
      return roll < 60 ? Sky.Rain : Sky.Storm;
    case Sky.Dust:
      // The sand settles as the wind drops (soon, not at once), or in a few hours anyway.
      if ((!windy && roll < 55) || roll < 18) return Sky.Clear;
      return Sky.Dust;
    default:
      // Fog or snow (an older save's, or a new map's first hour): it clears.
      return Sky.Clear;
  }
}
